/**
 * FIXED Infinite Scroll for Frappe Webshop
 * 
 * ✅ Works in BOTH grid and list views
 * ✅ Loads NEW products (not repeating same ones)
 * ✅ Properly tracks pagination
 */

function normalizeRoute(href) {
	if (!href) return null;
	try {
		return new URL(href, window.location.origin).pathname;
	} catch (e) {
		return href.startsWith('/') ? href : '/' + href;
	}
}

function extractCardKey(card) {
	// Card identity = the product ROUTE, never data-item-code: variant-swatch
	// scripts stamp cards with a selected VARIANT's code (e.g. JP-006-28) while
	// the listing API returns TEMPLATE codes (JP-006), so code-based identity
	// mismatches and lets the same product render twice.
	if (!card) return null;

	const link = card.querySelector('a[href]');
	const route = normalizeRoute(link && link.getAttribute('href'));
	if (route && route !== '/') return route;

	const byDataset = card.querySelector('[data-item-code]')?.dataset?.itemCode;
	if (byDataset) return byDataset;

	return card.querySelector('[data-item-code]')?.getAttribute('data-item-code') || null;
}

function itemKey(item) {
	// Server-item identity matching extractCardKey: route first, code fallback.
	const route = normalizeRoute(item && (item.route || null));
	if (route && route !== '/') return route;
	return (item && (item.item_code || item.name)) || null;
}

function buildQueryKeyFromUrl() {
	// A stable key representing the current query context (filters/sort/search).
	// Exclude `start` so paging increments don't cause resets.
	try {
		const url = new URL(window.location.href);
		const params = url.searchParams;
		const entries = [];
		const itemGroupEl = document.querySelector('.item-group-content[data-item-group]');
		const itemGroup = itemGroupEl ? itemGroupEl.getAttribute('data-item-group') : '';
		params.forEach((v, k) => {
			if (k === 'start') return;
			entries.push([k, v]);
		});
		if (itemGroup && !params.get('item_group')) {
			entries.push(['item_group', itemGroup]);
		}
		entries.sort((a, b) => (a[0] + '=' + a[1]).localeCompare(b[0] + '=' + b[1]));
		return `${url.pathname}?` + entries.map(([k, v]) => `${k}=${v}`).join('&');
	} catch (e) {
		return '';
	}
}

function markContextChanged(state) {
	// Called from context-change handlers (pushState/popstate/filter change).
	// Never reads the DOM: at this moment core webshop has not re-rendered yet,
	// so any capture here would freeze pre-swap state (the "repeat first page"
	// bug). Capture happens in the MutationObserver after the DOM settles.
	state.pendingContextReset = true;
	state.hasMore = true;
	state.replayGuardSkips = 0;
	state.firstLoadGuardApplied = false;
}

function captureStateFromDom(state) {
	// Recompute pagination state from the currently rendered cards. Only the
	// post-render observer (and the loadMore pre-flight guard) may call this.
	state.isLoading = false;
	state.hasMore = true;
	state.pageLength = null;
	state.loadedItemCodes = new Set();

	const currentProducts = document.querySelectorAll('.item-card');
	currentProducts.forEach((card) => {
		const itemCode = extractCardKey(card);
		if (itemCode) state.loadedItemCodes.add(itemCode);
	});
	state.start = state.loadedItemCodes.size;
	state.lastQueryKey = buildQueryKeyFromUrl();
	state.pendingContextReset = false;
}

frappe.ready(() => {
	// Run on core webshop listing contexts and preserve their current query scope.
	const isAllProductsPage = window.location.pathname === '/all-products';
	const isIndexPage = window.location.pathname === '/index';
	const isItemGroupListing = Boolean(document.querySelector('.item-group-content[data-item-group]'));
	const hasCatalogListingDom =
		Boolean(document.getElementById('product-listing')) &&
		(Boolean(document.querySelector('.item-card-group-section')) || Boolean(document.querySelector('.item-card')));
	
	// WebHQ extension: allow /index alias when it renders the same catalog listing DOM.
	if (!isAllProductsPage && !isItemGroupListing && !(isIndexPage && hasCatalogListingDom)) {
		return;
	}

	// Wait for page to fully load
	setTimeout(() => {
		initInfiniteScroll();
	}, 250);
});

function initInfiniteScroll() {
	if (window.__catalogExtensionsInfiniteScrollInitialized) {
		return;
	}
	window.__catalogExtensionsInfiniteScrollInitialized = true;

	const state = {
		isLoading: false,
		hasMore: true,
		start: 0,
		loadedItemCodes: new Set(), // Track which items we've already loaded
		pageLength: null,
		lastQueryKey: '',
		lastLoadAt: 0,
		minLoadIntervalMs: 350,
		preloadOffsetPx: 900,
		firstLoadGuardApplied: false,
		replayGuardSkips: 0,
		maxReplayGuardSkips: 3,
		pendingContextReset: false,
		selfMutation: false
	};

	// Count initial products and track their item codes
	const initialProducts = document.querySelectorAll('.item-card');
	initialProducts.forEach(card => {
		const itemCode = extractCardKey(card);
		if (itemCode) {
			state.loadedItemCodes.add(itemCode);
		}
	});
	
	state.start = state.loadedItemCodes.size;
	state.lastQueryKey = buildQueryKeyFromUrl();

	// Setup scroll listener
	let scrollTimeout;
	window.addEventListener('scroll', () => {
		clearTimeout(scrollTimeout);
		scrollTimeout = setTimeout(() => {
			handleScroll(state);
		}, 100);
	});

	// If the listing DOM is replaced (common on filter/sort actions), re-capture
	// state — this observer is the ONLY place that reads the DOM after a context
	// change, because it fires after core webshop finished re-rendering.
	const listingRoot = document.getElementById('product-listing');
	if (listingRoot && window.MutationObserver) {
		let resetTimer = null;
		const listingObserver = new MutationObserver(() => {
			if (resetTimer) window.clearTimeout(resetTimer);
			resetTimer = window.setTimeout(() => {
				// Our own appendProducts mutations must not clobber the server
				// cursor (next_start can exceed the DOM card count after
				// dedupe-overfetch) — skip unless a real context change is pending.
				if (state.selfMutation && !state.pendingContextReset) return;
				if (state.pendingContextReset || state.lastQueryKey !== buildQueryKeyFromUrl()) {
					captureStateFromDom(state);
				}
			}, 350);
		});
		listingObserver.observe(listingRoot, { childList: true, subtree: true });
	}

	// Mark state dirty when the listing context changes (filters/sort/search).
	// Capture is deferred to the observer above — the DOM is stale right now.
	window.addEventListener('popstate', () => markContextChanged(state));
	const origPushState = history.pushState;
	history.pushState = function () {
		origPushState.apply(this, arguments);
		markContextChanged(state);
	};
	const origReplaceState = history.replaceState;
	history.replaceState = function () {
		origReplaceState.apply(this, arguments);
		markContextChanged(state);
	};

	// Many webshop filters trigger a navigation (window.location.href). For UI that
	// mutates the URL without reload, also mark dirty on filter checkbox changes.
	document.addEventListener('change', (e) => {
		if (e.target && e.target.closest && e.target.closest('.product-filter')) {
			markContextChanged(state);
		}
	});

	// Listen for view toggle
	document.addEventListener('click', (e) => {
		if (e.target.closest('.btn-grid-view, .btn-list-view, #grid, #list')) {
			// Update start count after view change
			setTimeout(() => {
				document.querySelectorAll('.item-card');
			}, 100);
		}
	});

	// If the first page is shorter than the viewport, start loading immediately.
	// Wait for core webshop rendering to populate initial cards first, otherwise
	// we can accidentally request page 1 again with start=0.
	scheduleInitialScrollCheck(state);
}

function normalizeInitialStartFromDomFallback(state) {
	const cards = document.querySelectorAll('.item-card');
	if (!cards || !cards.length) return;
	if (state.start > 0) return;

	// WebHQ bridge: prevent first-page replay when core cards are rendered before
	// item-code extraction is fully reliable. Remove if core exposes initial offset.
	state.start = cards.length;
}

function scheduleInitialScrollCheck(state) {
	var attempts = 0;
	var maxAttempts = 20;
	var intervalMs = 150;

	function run() {
		attempts += 1;
		captureStateFromDom(state);
		normalizeInitialStartFromDomFallback(state);

		// Only run the initial autoload when initial products are already present.
		if (state.start > 0) {
			handleScroll(state);
			return;
		}

		if (attempts < maxAttempts) {
			window.setTimeout(run, intervalMs);
		}
	}

	window.setTimeout(run, 200);
}

function handleScroll(state) {
	if (state.isLoading || !state.hasMore) {
		return;
	}

	// Prevent rapid re-triggers when the page is short or append changes scrollHeight.
	const now = Date.now();
	if (state.lastLoadAt && now - state.lastLoadAt < state.minLoadIntervalMs) {
		return;
	}

	const scrollPosition = window.innerHeight + window.scrollY;
	const documentHeight = Math.max(
		document.body.scrollHeight,
		document.documentElement.scrollHeight,
		document.body.offsetHeight,
		document.documentElement.offsetHeight
	);
	const threshold = documentHeight - state.preloadOffsetPx;

	if (scrollPosition >= threshold) {
		loadMore(state);
	}
}

function loadMore(state) {
	// Pre-flight guard, re-armed by every markContextChanged: if a context reset
	// is still pending, or cards exist while start stayed zero, capture from the
	// DOM and skip this cycle to avoid requesting page 1 again.
	if (!state.firstLoadGuardApplied) {
		state.firstLoadGuardApplied = true;
		if (
			state.pendingContextReset ||
			(state.start === 0 && document.querySelectorAll('.item-card').length > 0)
		) {
			captureStateFromDom(state);
			normalizeInitialStartFromDomFallback(state);
			return;
		}
	}

	state.isLoading = true;
	state.lastLoadAt = Date.now();
	showLoading();

	// Get current filters from URL and page
	const filters = getCurrentFilters();
	const discountFilterActive = isDiscountFilterActive(filters);

	// Call Frappe API
	// NOTE: This method is overridden in raftor_webhq to accept a single
	// `query_args` dict (mirroring core signature). Passing raw args would drop
	// `start` and cause the API to always return page 1.
	frappe.call({
		method: 'webshop.webshop.api.get_product_filter_data',
		args: {
			query_args: {
				start: state.start,
				from_filters: 0,
				...filters
			}
		},
		callback: (r) => {
			hideLoading();
			const totalCount = Number(r?.message?.items_count);
			const countReliable = Number(r?.message?.count_reliable);
			const hasTotalCount =
				!discountFilterActive &&
				countReliable !== 0 &&
				Number.isFinite(totalCount) &&
				totalCount >= 0;
			const serverNextStart = Number(r?.message?.next_start);
			const hasServerNextStart = Number.isFinite(serverNextStart) && serverNextStart >= 0;
			// Authoritative pagination signal from the server. When present it
			// decides "more rows exist" — no data-shape guessing needed.
			const rawHasMore = r?.message?.has_more;
			const hasServerHasMore = rawHasMore !== undefined && rawHasMore !== null;
			const serverHasMore = Boolean(Number(rawHasMore));
			// Page size comes from server settings, never inferred from data.
			const serverPageLen = Number(r?.message?.settings?.products_per_page);
			if (!state.pageLength && Number.isFinite(serverPageLen) && serverPageLen > 0) {
				state.pageLength = serverPageLen;
			}

			// Termination precedence: server has_more > reliable total count >
			// legacy short-page heuristic (only when talking to an older server).
			const evaluateHasMore = (newItemsLength) => {
				if (hasServerHasMore) return serverHasMore;
				if (hasTotalCount) return state.loadedItemCodes.size < totalCount;
				if (!state.pageLength) state.pageLength = newItemsLength;
				return newItemsLength >= state.pageLength;
			};

			if (r.message && r.message.items && r.message.items.length > 0) {
				const newItems = r.message.items;

				// Filter out duplicates (safety net only — never a terminator).
				// Keys are routes (see extractCardKey/itemKey) so identity survives
				// variant-swatch scripts overwriting card data-item-code attributes.
				const uniqueNewItems = newItems.filter(item => {
					const key = itemKey(item);
					if (key && state.loadedItemCodes.has(key)) {
						return false;
					}
					if (key) state.loadedItemCodes.add(key);
					return true;
				});

				// Use backend cursor when provided to avoid pagination drift with dedup overfetch.
				state.start = hasServerNextStart ? serverNextStart : (state.start + newItems.length);
				state.hasMore = evaluateHasMore(newItems.length);

				if (uniqueNewItems.length === 0) {
					// Whole page was already displayed. With a server signal this is
					// just a skip; without one, retry a bounded number of times.
					state.replayGuardSkips += 1;
					if (!hasServerHasMore && !hasTotalCount &&
						state.replayGuardSkips > state.maxReplayGuardSkips) {
						state.hasMore = false;
					}
				} else {
					state.replayGuardSkips = 0;
					// Append products to BOTH views
					appendProducts(uniqueNewItems, r.message.settings || {}, state);
				}

				if (!state.hasMore) {
					showEndMessage(state.loadedItemCodes.size, totalCount, hasServerHasMore || hasTotalCount);
				}
			} else {
				state.hasMore = false;
				showEndMessage(state.loadedItemCodes.size, totalCount, hasTotalCount);
			}

			state.isLoading = false;
			if (state.hasMore) {
				queueBottomCheck(state);
			}
		},
		error: (err) => {
			hideLoading();
			state.isLoading = false;
			showError();
		}
	});
}

function queueBottomCheck(state) {
	window.setTimeout(() => {
		handleScroll(state);
	}, state.minLoadIntervalMs);
}

function isDiscountFilterActive(filters) {
	try {
		const raw = filters && filters.field_filters;
		if (!raw) return false;
		const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
		const discount = parsed && parsed.discount;
		return Array.isArray(discount) ? discount.length > 0 : !!discount;
	} catch (e) {
		return false;
	}
}

function getCurrentFilters() {
	const filters = {};

	// Prefer URL params (authoritative for current listing state)
	const urlParams = (frappe?.utils?.get_query_params && frappe.utils.get_query_params()) || {};
	const itemGroupEl = document.querySelector('.item-group-content[data-item-group], [data-item-group]');

	// Standard top-level params
	if (urlParams.search) filters.search = urlParams.search;
	if (urlParams.item_group) filters.item_group = urlParams.item_group;
	if (urlParams.brand) filters.brand = urlParams.brand;

	// Item-group listing pages usually carry the group in page markup, not the URL.
	if (!filters.item_group && itemGroupEl) {
		const itemGroup = itemGroupEl.getAttribute('data-item-group');
		if (itemGroup) {
			filters.item_group = itemGroup;
		}
	}

	// Preserve core JSON params if present
	if (urlParams.field_filters) filters.field_filters = urlParams.field_filters;
	if (urlParams.attribute_filters) filters.attribute_filters = urlParams.attribute_filters;

	// Also accept price filters (our override reads these from q or field_filters)
	if (urlParams.price_from) filters.price_from = urlParams.price_from;
	if (urlParams.price_to) filters.price_to = urlParams.price_to;

	// Fallback: if URL doesn't have search but input does, use input
	if (!filters.search) {
		const searchInput = document.querySelector('input[name="query"], .product-search');
		if (searchInput && searchInput.value) {
			filters.search = searchInput.value;
		}
	}

	return filters;
}

function appendProducts(products, settings, state) {
	// Our own DOM insertions must not be mistaken for a listing re-render by the
	// MutationObserver (which would clobber the server cursor). Flag them for
	// longer than the observer's 350ms debounce.
	if (state) {
		state.selfMutation = true;
		window.setTimeout(() => {
			state.selfMutation = false;
		}, 500);
	}

	// ✅ FIXED: Find BOTH grid and list containers
	const gridArea = document.getElementById('products-grid-area');
	const listArea = document.getElementById('products-list-area');
	
	let gridContainer = null;
	let listContainer = null;
	let gridExists = false;
	let listExists = false;

	function resolveContainer(areaEl) {
		if (!areaEl) return null;
		// If the area itself is already the row/container, append directly into it.
		if (areaEl.classList && areaEl.classList.contains('row')) {
			return areaEl;
		}
		return (
			areaEl.querySelector('.products-list') ||
			areaEl.querySelector('.row') ||
			areaEl.querySelector('.item-card-group-section .row') ||
			areaEl.querySelector('.item-card-group-section')
		);
	}
	
	// Check which views exist and are active
	if (gridArea) {
		gridContainer = resolveContainer(gridArea);
		// If grid area exists but no obvious container exists (depends on theme/templates),
		// create a .row container so appended cards have a consistent parent.
		if (!gridContainer) {
			gridContainer = document.createElement('div');
			gridContainer.className = 'row';
			gridArea.appendChild(gridContainer);
		}
		gridExists = !!gridContainer;
	}
	
	if (listArea) {
		listContainer = resolveContainer(listArea);
		if (!listContainer) {
			listContainer = document.createElement('div');
			listContainer.className = 'row';
			listArea.appendChild(listContainer);
		}
		listExists = !!listContainer;
	}

	// Fallback: if we can't determine active view, find any .products-list
	if (!gridContainer && !listContainer) {
		const fallbackContainer = document.querySelector('.products-list, .row');
		if (fallbackContainer) {
			gridContainer = fallbackContainer;
			gridExists = true;
		}
	}

	if (!gridExists && !listExists) {
		return;
	}

	// ✅ FIXED: Append to BOTH views (they render independently)
	const containerHasCard = (container, product) => {
		// DOM-level dedupe by product ROUTE — data-item-code is unreliable
		// (variant-swatch scripts stamp variant codes on template cards).
		const route = itemKey(product);
		if (route && route.startsWith('/')) {
			const links = container.querySelectorAll('.item-card a[href]');
			for (const a of links) {
				if (normalizeRoute(a.getAttribute('href')) === route) return true;
			}
			return false;
		}
		const code = product.item_code || product.name || '';
		return Boolean(code && container.querySelector(`[data-item-code="${CSS.escape(code)}"]`));
	};

	products.forEach(product => {
		// Regardless of any state race upstream, a card that is already
		// rendered must never be inserted twice.

		// Append to grid if it exists
		if (gridContainer && gridExists && !containerHasCard(gridContainer, product)) {
			const gridHtml = createProductHTML(product, 'grid', settings);
			gridContainer.insertAdjacentHTML('beforeend', gridHtml);
		}

		// Append to list if it exists
		if (listContainer && listExists && !containerHasCard(listContainer, product)) {
			const listHtml = createProductHTML(product, 'list', settings);
			listContainer.insertAdjacentHTML('beforeend', listHtml);
		}
	});

	// Reinitialize cart handlers
	if (window.webshop && webshop.shopping_cart) {
		webshop.shopping_cart.bind_add_to_cart_action();
	}

	if (window.webshop && webshop.webshop && webshop.webshop.wishlist) {
		webshop.webshop.wishlist.bind_wishlist_action();
	}

	// Trigger custom event
	document.dispatchEvent(new CustomEvent('products-loaded', { 
		detail: { count: products.length } 
	}));

	// Allow other listing enhancement scripts to re-run on newly appended cards
	if (typeof window.catalogExtensionsOnProductsLoaded === 'function') {
		try {
			window.catalogExtensionsOnProductsLoaded({ count: products.length });
		} catch (e) {
			// ignore
		}
	}
}

function createProductHTML(product, viewType, settings) {
	const name = product.item_name || product.web_item_name || '';
	const code = product.item_code || product.name || '';
	const route = product.route || `/product/${code}`;
	const href = route && route.startsWith('/') ? route : `/${route}`;
	const image = product.website_image || product.image || '/assets/frappe/images/default-image.svg';
	const priceHtml = getPriceHTML(product);
	const inStock = Boolean(product.in_stock);
	const inCart = Boolean(product.in_cart);
	const allowItemsNotInStock = Boolean(settings && settings.allow_items_not_in_stock);
	const showStockAvailability = Boolean(settings && settings.show_stock_availability);
	const canAddToCart = !showStockAvailability || allowItemsNotInStock || inStock;
	const shortDesc = product.short_description || '';
	const category = product.item_group || product.item_group_name || product.item_group_title || '';
	const wishlistEnabled = Boolean(settings && settings.enable_wishlist) && !Boolean(product.has_variants);
	const wished = Boolean(product.wished);
	const wishlistIconClass = wished ? 'wished' : 'not-wished';
	const wishlistWrapperClass = wished ? 'like-action-wished' : '';
	const wishlistHtml = wishlistEnabled
		? (
			viewType === 'list'
				? `
					<div class="like-action-list ${wishlistWrapperClass}" data-item-code="${code}">
						<svg class="icon sm">
							<use class="${wishlistIconClass} wish-icon" href="#icon-heart"></use>
						</svg>
					</div>
				`
				: `
					<div class="like-action ${wishlistWrapperClass}" data-item-code="${code}">
						<svg class="icon sm">
							<use class="${wishlistIconClass} wish-icon" href="#icon-heart"></use>
						</svg>
					</div>
				`
		)
		: '';

	if (viewType === 'list') {
		return `
			<div class="col-12 item-card" data-item-code="${code}">
				<div class="card list-row" data-item-code="${code}">
					<div class="row no-gutters">
						<div class="col-sm-3">
							<a href="${href}">
								<img src="${image}" alt="${name}" class="list-image" loading="lazy">
							</a>
							${wishlistHtml}
						</div>
						<div class="col-sm-9">
							<div class="card-body">
								<h5 class="card-title">
									<a href="${href}">${name}</a>
								</h5>
								${category ? `<div class="product-category" itemprop="name">${category}</div>` : ''}
								${shortDesc ? `<p class="card-text">${shortDesc}</p>` : ''}
								${priceHtml}
								${showStockAvailability && !inStock ? '<div class="out-of-stock">Out of Stock</div>' : ''}
								${canAddToCart ? `
									<div class="btn btn-sm btn-primary btn-add-to-cart-list ${inCart ? 'hidden' : ''}"
										data-item-code="${code}">
										Add to Cart
									</div>
									<div class="cart-indicator list-indicator ${inCart ? '' : 'hidden'}"
										data-item-code="${code}">
										1
									</div>
									<a href="/cart">
										<div class="btn btn-sm btn-primary btn-add-to-cart-list ml-4 go-to-cart mb-0 mt-0 ${inCart ? '' : 'hidden'}"
											data-item-code="${code}">
											Go to Cart
										</div>
									</a>
								` : ''}
							</div>
						</div>
					</div>
				</div>
			</div>
		`;
	} else {
		// Grid view
		return `
			<div class="col-sm-4 item-card" data-item-code="${code}"><div class="card text-left" data-item-code="${code}">
					<div class="card-img-container">
						<a href="${href}" style="text-decoration: none;">
							<img class="card-img" src="${image}" alt="${name}" loading="lazy">
						</a>
					</div>
					<div class="card-body text-left card-body-flex" style="width:100%">
						<div style="margin-top: 1rem; display: flex;">
							<a href="${href}">
								<div class="product-title" itemprop="name">${name}</div>
							</a>
							${wishlistHtml}
							<div class="cart-indicator ${inCart ? '' : 'hidden'}" data-item-code="${code}">1</div>
						</div>
						${category ? `<div class="product-category" itemprop="name">${category}</div>` : ''}
						${priceHtml}
						${showStockAvailability && !inStock ? '<div class="out-of-stock">Out of Stock</div>' : ''}
						${canAddToCart ? `
							<div class="btn btn-sm btn-primary btn-add-to-cart-list w-100 mt-2 ${inCart ? 'hidden' : ''}"
								data-item-code="${code}">
								<span class="mr-2">
									<svg class="icon icon-md">
										<use href="#icon-assets"></use>
									</svg>
								</span>
								Add to Cart
							</div>
							<a href="/cart">
								<div class="btn btn-sm btn-primary btn-add-to-cart-list w-100 mt-4 go-to-cart-grid ${inCart ? '' : 'hidden'}"
									data-item-code="${code}">
									Go to Cart
								</div>
							</a>
						` : ''}
					</div>
				</div></div>
		`;
	}
}

function getPriceHTML(product) {
	// Delegate to the card-contract shared ribbon (product_card_contract.js) so
	// appended cards render identically to swatch-rendered ones.
	if (!product || !window.webhqPriceRibbon) {
		return '';
	}
	return window.webhqPriceRibbon(product);
}

function showLoading() {
	let loader = document.getElementById('infinite-scroll-loader');
	if (!loader) {
		loader = document.createElement('div');
		loader.id = 'infinite-scroll-loader';
		loader.className = 'infinite-scroll-loading';
		loader.innerHTML = '<div class="spinner"></div><p>Loading more products...</p>';
		
		const container = document.getElementById('product-listing') || 
		                  document.querySelector('.products-list')?.parentElement ||
		                  document.body;
		container.appendChild(loader);
	}
	loader.style.display = 'block';
}

function hideLoading() {
	const loader = document.getElementById('infinite-scroll-loader');
	if (loader) {
		loader.style.display = 'none';
	}
}

function showEndMessage(loadedCount, totalCount, countReliable) {
	let msg = document.getElementById('infinite-scroll-end');
	const safeLoadedCount = Number.isFinite(Number(loadedCount)) ? Number(loadedCount) : 0;
	const safeTotalCount = Number.isFinite(Number(totalCount)) ? Number(totalCount) : safeLoadedCount;
	const displayCount = countReliable ? safeTotalCount : safeLoadedCount;
	const summaryText = `All products loaded (${displayCount} items).`;
	if (!msg) {
		msg = document.createElement('div');
		msg.id = 'infinite-scroll-end';
		msg.className = 'infinite-scroll-end';
		msg.innerHTML = `<p>${summaryText}</p>`;
		
		const container = document.getElementById('product-listing') || 
		                  document.querySelector('.products-list')?.parentElement ||
		                  document.body;
		container.appendChild(msg);
	} else {
		msg.innerHTML = `<p>${summaryText}</p>`;
	}
	msg.style.display = 'block';
}

function showError() {
	let error = document.getElementById('infinite-scroll-error');
	if (!error) {
		error = document.createElement('div');
		error.id = 'infinite-scroll-error';
		error.style.cssText = 'text-align: center; padding: 2rem; color: #dc3545;';
		error.innerHTML = '<p>Unable to load more products. Please try again.</p>';
		
		const container = document.getElementById('product-listing') || 
		                  document.querySelector('.products-list')?.parentElement ||
		                  document.body;
		container.appendChild(error);
	}
	error.style.display = 'block';
	
	setTimeout(() => {
		error.style.display = 'none';
	}, 5000);
}
